import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Zap, Package, ShoppingBag, Check, Minus, Plus, Ruler } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import BandVisual from "@/components/BandVisual";
import { FadeUp } from "@/components/motion-ui";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const products = [
  {
    id: "elastique-vert",
    name: "Élastique Vert",
    resistance: "10 kg",
    level: "Débutante",
    dimensions: "4 cm × 105 cm",
    pantone: "PANTONE 3405C",
    price: 15,
    features: [
      "Idéal pour l'échauffement",
      "Prise en main progressive",
      "Rééducation & mobilité",
    ],
    band: { color: "#00A34A" },
    styles: {
      bg: "bg-emerald-50",
      accent: "text-emerald-600",
      button: "bg-emerald-600 hover:bg-emerald-700",
      badge: "bg-emerald-600 text-white",
    },
  },
  {
    id: "elastique-rouge",
    name: "Élastique Rouge",
    resistance: "15 kg",
    level: "Intermédiaire",
    dimensions: "4 cm × 105 cm",
    pantone: "PANTONE 2028C",
    price: 18,
    features: [
      "Fessiers & cuisses ciblés",
      "Le plus polyvalent",
      "Bon compromis force/confort",
    ],
    band: { color: "#E4002B" },
    styles: {
      bg: "bg-red-50",
      accent: "text-red-600",
      button: "bg-red-600 hover:bg-red-700",
      badge: "bg-red-600 text-white",
    },
  },
  {
    id: "elastique-bleu",
    name: "Élastique Bleu",
    resistance: "20 kg",
    level: "Avancée",
    dimensions: "5 cm × 105 cm",
    pantone: "PANTONE 2738C",
    price: 22,
    features: [
      "Résistance intense",
      "Renforcement profond",
      "Pour les warriors 💪",
    ],
    band: { color: "#1E22AA" },
    styles: {
      bg: "bg-blue-50",
      accent: "text-blue-600",
      button: "bg-blue-700 hover:bg-blue-800",
      badge: "bg-blue-700 text-white",
    },
  },
  {
    id: "trio-band",
    name: "TRIO BAND",
    resistance: "15 + 20 + 30 kg",
    level: "Multi-zones",
    dimensions: "5 cm × 105 cm",
    pantone: "3-en-1 signature",
    price: 45,
    save: "Économise 10€",
    features: [
      "3 zones de résistance en 1 bande",
      "Rouge 15kg • Bleu 20kg • Vert 30kg",
      "L'expérience complète Perle Lama",
    ],
    band: {
      tri: [
        { color: "#E4002B", label: "15kg" },
        { color: "#1E22AA", label: "20kg" },
        { color: "#00A34A", label: "30kg" },
      ],
    },
    styles: {
      bg: "bg-gradient-to-br from-red-50 via-blue-50 to-emerald-50",
      accent: "text-fuchsia-600",
      button: "bg-fuchsia-600 hover:bg-fuchsia-700",
      badge: "bg-fuchsia-600 text-white",
    },
    featured: true,
  },
];

export default function PreOrder() {
  const [openId, setOpenId] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    quantity: 1,
    note: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const activeProduct = products.find((p) => p.id === openId);

  const openDialog = (product) => {
    setOpenId(product.id);
    setForm({ name: "", email: "", phone: "", quantity: 1, note: "" });
    setDone(false);
  };

  const closeDialog = (v) => {
    if (!v) {
      setOpenId(null);
      setDone(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!activeProduct) return;
    if (!form.name || !form.email) {
      toast.error("Merci de renseigner ton nom et ton email ✨");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/preorder`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        product_id: activeProduct.id,
        product_name: activeProduct.name,
        quantity: form.quantity,
        note: form.note,
      });
      setDone(true);
      toast.success(
        `Précommande enregistrée : ${activeProduct.name} x${form.quantity} 💖`
      );
    } catch (err) {
      toast.error("Oups, réessaie ou contacte-nous à lagigafam@gmail.com");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="precommandes"
      data-testid="preorder-section"
      className="relative px-5 md:px-8 py-24 md:py-32 bg-gradient-to-b from-white via-fuchsia-50/40 to-white overflow-hidden"
    >
      <div className="absolute top-40 -left-20 w-[380px] h-[380px] blob-pink pointer-events-none opacity-70" />
      <div className="absolute bottom-20 -right-20 w-[320px] h-[320px] blob-cyan pointer-events-none" />

      <div className="relative max-w-7xl mx-auto">
        <FadeUp className="text-center mb-14">
          <span className="inline-flex items-center gap-2 bg-pink-600 text-white text-xs font-bold uppercase tracking-[0.25em] px-4 py-2 rounded-full mb-4 shadow-lg">
            <Zap size={14} /> Précommande ouverte
          </span>
          <h2 className="font-display text-4xl md:text-6xl font-black uppercase tracking-tight text-neutral-900 leading-[0.95]">
            Élastiques
            <br />
            <span className="text-pink-600">LAGIGAFAM.</span>
          </h2>
          <p className="mt-5 max-w-2xl mx-auto text-lg text-neutral-600 font-medium">
            Trois résistances calibrées + le TRIO BAND signature. Bandes
            professionnelles 105 cm, avec numérotation 1-6 pour tes séances
            guidées. Livraison prioritaire sur les précommandes 🚚
          </p>
        </FadeUp>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {products.map((p, idx) => (
            <motion.article
              key={p.id}
              data-testid={`product-card-${p.id}`}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.9, delay: idx * 0.1, ease: [0.22, 0.8, 0.3, 1] }}
              whileHover={{ y: -8 }}
              className={`${p.styles.bg} relative rounded-[2rem] border ${
                p.featured
                  ? "border-fuchsia-300 ring-4 ring-fuchsia-200/60"
                  : "border-pink-100"
              } p-6 flex flex-col hover:shadow-[0_20px_60px_-10px_rgba(255,0,127,0.35)] transition-shadow duration-500`}
            >
              {p.featured && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-fuchsia-600 text-white text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full shadow-md">
                  ⭐ Best value
                </span>
              )}

              <div className="mb-5 pt-2">
                <BandVisual
                  color={p.band.color}
                  tri={p.band.tri}
                  label={p.pantone}
                />
              </div>

              <div className="flex items-center gap-2 flex-wrap mb-2">
                <span
                  className={`${p.styles.badge} text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full`}
                >
                  {p.resistance}
                </span>
                <span className="text-[10px] font-bold uppercase tracking-widest text-neutral-500 flex items-center gap-1">
                  <Ruler size={11} /> {p.dimensions}
                </span>
              </div>

              <h3 className="font-display text-2xl font-black text-neutral-900 leading-tight">
                {p.name}
              </h3>
              <p className={`text-sm font-semibold ${p.styles.accent} mt-1`}>
                {p.level}
              </p>

              <ul className="mt-4 space-y-2 flex-grow">
                {p.features.map((f, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-sm text-neutral-700 font-medium"
                  >
                    <Check
                      size={16}
                      className={`${p.styles.accent} shrink-0 mt-0.5`}
                    />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-5 flex items-end justify-between gap-3">
                <div>
                  <p
                    className={`font-display text-3xl font-black ${p.styles.accent}`}
                  >
                    {p.price}€
                  </p>
                  {p.save && (
                    <p className="text-[11px] font-bold uppercase tracking-widest text-fuchsia-600 mt-0.5">
                      {p.save}
                    </p>
                  )}
                </div>
                <Button
                  data-testid={`preorder-button-${p.id}`}
                  onClick={() => openDialog(p)}
                  className={`${p.styles.button} text-white rounded-full h-11 px-4 font-bold text-sm shadow-md`}
                >
                  <ShoppingBag size={16} className="mr-1" />
                  Précommander
                </Button>
              </div>
            </motion.article>
          ))}
        </div>

        <div className="mt-10 text-center">
          <p className="text-sm text-neutral-500 font-medium">
            <Package size={14} className="inline -mt-0.5 mr-1" />
            Paiement à la livraison ou par virement — instructions envoyées par
            email après précommande.
          </p>
        </div>
      </div>

      <Dialog open={!!openId} onOpenChange={closeDialog}>
        <DialogContent
          data-testid="preorder-dialog"
          className="max-w-md rounded-3xl border-pink-200 bg-white p-0 overflow-hidden"
        >
          <div className="bg-pink-600 text-white p-6">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-black uppercase">
                {done ? "Précommande reçue 💖" : activeProduct?.name}
              </DialogTitle>
              <DialogDescription className="text-white/90 mt-1">
                {done
                  ? "Perle et son équipe te recontactent très vite avec les détails de livraison et de paiement."
                  : `${activeProduct?.resistance} • ${activeProduct?.price}€ / unité`}
              </DialogDescription>
            </DialogHeader>
          </div>

          {done ? (
            <div className="p-6 text-center">
              <div className="mx-auto h-16 w-16 rounded-full bg-pink-100 flex items-center justify-center mb-4">
                <Check size={32} className="text-pink-600" />
              </div>
              <p className="text-neutral-700 font-medium">
                Ta précommande{" "}
                <strong>
                  {activeProduct?.name} x{form.quantity}
                </strong>{" "}
                est enregistrée ✨
              </p>
              <Button
                data-testid="preorder-close-button"
                onClick={() => closeDialog(false)}
                className="mt-6 rounded-full bg-pink-600 hover:bg-pink-700 text-white h-12 px-6 font-bold w-full"
              >
                Continuer à explorer
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="p-6 space-y-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-neutral-600">
                  Nom *
                </label>
                <Input
                  data-testid="preorder-input-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1 h-11 rounded-full border-pink-200 focus-visible:ring-pink-500 focus-visible:ring-2 px-5"
                  required
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-neutral-600">
                  Email *
                </label>
                <Input
                  data-testid="preorder-input-email"
                  type="email"
                  value={form.email}
                  onChange={(e) =>
                    setForm({ ...form, email: e.target.value })
                  }
                  className="mt-1 h-11 rounded-full border-pink-200 focus-visible:ring-pink-500 focus-visible:ring-2 px-5"
                  required
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-neutral-600">
                  Téléphone
                </label>
                <Input
                  data-testid="preorder-input-phone"
                  value={form.phone}
                  onChange={(e) =>
                    setForm({ ...form, phone: e.target.value })
                  }
                  className="mt-1 h-11 rounded-full border-pink-200 focus-visible:ring-pink-500 focus-visible:ring-2 px-5"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-neutral-600">
                  Quantité
                </label>
                <div className="mt-1 flex items-center gap-3">
                  <button
                    type="button"
                    data-testid="preorder-qty-minus"
                    onClick={() =>
                      setForm({
                        ...form,
                        quantity: Math.max(1, form.quantity - 1),
                      })
                    }
                    className="h-11 w-11 rounded-full bg-pink-50 hover:bg-pink-100 border border-pink-200 flex items-center justify-center"
                  >
                    <Minus size={16} className="text-pink-600" />
                  </button>
                  <div
                    data-testid="preorder-qty-value"
                    className="h-11 flex-1 rounded-full border border-pink-200 flex items-center justify-center font-display text-xl font-black text-neutral-900"
                  >
                    {form.quantity}
                  </div>
                  <button
                    type="button"
                    data-testid="preorder-qty-plus"
                    onClick={() =>
                      setForm({
                        ...form,
                        quantity: Math.min(50, form.quantity + 1),
                      })
                    }
                    className="h-11 w-11 rounded-full bg-pink-50 hover:bg-pink-100 border border-pink-200 flex items-center justify-center"
                  >
                    <Plus size={16} className="text-pink-600" />
                  </button>
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-neutral-600">
                  Message (optionnel)
                </label>
                <Textarea
                  data-testid="preorder-input-note"
                  value={form.note}
                  onChange={(e) =>
                    setForm({ ...form, note: e.target.value })
                  }
                  rows={3}
                  placeholder="Adresse, préférences, questions..."
                  className="mt-1 rounded-2xl border-pink-200 focus-visible:ring-pink-500 focus-visible:ring-2 px-5 py-3"
                />
              </div>

              <div className="flex items-center justify-between bg-pink-50 rounded-2xl p-4">
                <span className="text-sm font-bold text-neutral-700">
                  Total
                </span>
                <span className="font-display text-2xl font-black text-pink-600">
                  {(activeProduct?.price || 0) * form.quantity}€
                </span>
              </div>

              <DialogFooter className="pt-2">
                <Button
                  data-testid="preorder-submit-button"
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 rounded-full bg-pink-600 hover:bg-pink-700 text-white font-bold shadow-lg"
                >
                  {loading ? "Envoi..." : "Confirmer la précommande ✨"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
